import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faEnvelope, faTimes } from '@/icons';
import { useTheme } from '../contexts/ThemeContext';
import { CustomTooltip } from './CustomTooltip';
import { NotificationModal } from './NotificationModal';
import {
    notificationApiService,
    NotificationItem,
    NOTIFICATION_BELL_UNREAD_TAKE,
} from '../services/notificationApiService';
import { useWizardNavigation } from '../hooks/useWizardNavigation';
import { SaveConfirmationModal } from './SaveConfirmationModal';
import { debugError } from '../utils/debug';
import { renderTextWithLinks } from '../utils/renderTextWithLinks';
import { htmlToPlainText } from '../utils/notificationHtml';

interface NotificationBellMenuProps {
    unreadNotificationCount: number;
}

export const NotificationBellMenu: React.FC<NotificationBellMenuProps> = ({
    unreadNotificationCount,
}) => {
    const theme = useTheme();
    // useWizardNavigation is a stateful hook — this call gets its OWN showSaveModal/
    // pendingNavigation state, independent of Navigation.tsx's instance (the parent that
    // renders this component). Navigation.tsx's <SaveConfirmationModal> only responds to
    // its own instance's state, so this component must render its own modal too, or
    // navigateTo() silently does nothing whenever the unsaved-changes gate actually triggers
    // (manual mode, or a save failure in autosave mode) — the click closes the panel and then
    // nothing happens, since the guard is waiting on a modal nobody ever renders.
    const { navigateTo, showSaveModal, isSaving: isSavingBeforeNav, handleSave, handleDiscard, handleCancel } = useWizardNavigation();
    const containerRef = useRef<HTMLDivElement>(null);
    const [showPanel, setShowPanel] = useState(false);
    const [showBellTooltip, setShowBellTooltip] = useState(false);
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState<NotificationItem[]>([]);
    const [selectedNotification, setSelectedNotification] = useState<NotificationItem | null>(null);
    const [togglingIds, setTogglingIds] = useState<Set<string>>(new Set());
    const [markingAllRead, setMarkingAllRead] = useState(false);

    const loadUnread = useCallback(async () => {
        try {
            setLoading(true);
            const response = await notificationApiService.listNotifications({
                unreadOnly: true,
                take: NOTIFICATION_BELL_UNREAD_TAKE,
            });
            if (response.success) {
                setItems(response.notifications ?? []);
            }
        } catch (error) {
            debugError('Error loading notification bell list:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!showPanel) return;
        void loadUnread();
    }, [showPanel, loadUnread]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setShowPanel(false);
            }
        };
        if (showPanel) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showPanel]);

    const handleBellClick = () => {
        setShowPanel(prev => !prev);
    };

    const handleMarkRead = async (notification: NotificationItem, e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            setTogglingIds(prev => new Set(prev).add(notification.notificationId));
            const result = await notificationApiService.toggleRead(notification.notificationId, true);
            if (result.success) {
                setItems(prev => prev.filter(n => n.notificationId !== notification.notificationId));
                window.dispatchEvent(new Event('notification-read'));
            }
        } catch (error) {
            debugError('Error marking notification read:', error);
        } finally {
            setTogglingIds(prev => {
                const next = new Set(prev);
                next.delete(notification.notificationId);
                return next;
            });
        }
    };

    const handleMarkAllRead = async () => {
        if ((items.length === 0 && unreadNotificationCount === 0) || markingAllRead) return;
        try {
            setMarkingAllRead(true);
            const result = await notificationApiService.markAllRead();
            if (result.success) {
                setItems([]);
                window.dispatchEvent(new Event('notification-read'));
            }
        } catch (error) {
            debugError('Error marking all notifications read:', error);
        } finally {
            setMarkingAllRead(false);
        }
    };

    const handleRowClick = (notification: NotificationItem) => {
        setSelectedNotification(notification);
        setShowPanel(false);
    };

    const handleCloseNotificationModal = async () => {
        const notification = selectedNotification;
        setSelectedNotification(null);
        if (notification && !notification.isRead) {
            try {
                const result = await notificationApiService.toggleRead(notification.notificationId, true);
                if (result.success) {
                    window.dispatchEvent(new Event('notification-read'));
                }
            } catch (error) {
                debugError('Error marking notification read on modal close:', error);
            }
        }
    };

    const truncate = (text: string, max: number) => {
        const t = (text || '').trim();
        if (t.length <= max) return t;
        return `${t.slice(0, max - 1)}…`;
    };

    return (
        <>
            <div ref={containerRef} style={{ position: 'relative' }}>
                <CustomTooltip
                    text={
                        unreadNotificationCount > 0
                            ? `Notifications (${unreadNotificationCount})`
                            : 'Notifications'
                    }
                    position="bottom"
                    show={showBellTooltip}
                >
                    <button
                        type="button"
                        data-tutorial-target="notification-bell"
                        style={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: theme.spacing.sm,
                            borderRadius: theme.borderRadius.sm,
                            transition: `all ${theme.transitions.normal}`,
                        }}
                        onClick={handleBellClick}
                        onMouseEnter={e => {
                            e.currentTarget.style.backgroundColor = theme.colors.primary + '10';
                            setShowBellTooltip(true);
                        }}
                        onMouseLeave={e => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            setShowBellTooltip(false);
                        }}
                    >
                        <FontAwesomeIcon
                            icon={faBell}
                            style={{
                                fontSize: theme.typography.fontSize.lg,
                                color: theme.colors.text.primary,
                            }}
                        />
                        {unreadNotificationCount > 0 && (
                            <span
                                style={{
                                    position: 'absolute',
                                    top: '2px',
                                    right: '2px',
                                    backgroundColor: '#ef4444',
                                    color: '#ffffff',
                                    fontSize: '10px',
                                    fontWeight: 700,
                                    minWidth: '16px',
                                    height: '16px',
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '0 4px',
                                    lineHeight: 1,
                                }}
                            >
                                {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                            </span>
                        )}
                    </button>
                </CustomTooltip>

                {showPanel && (
                    <div
                        style={{
                            position: 'absolute',
                            top: '100%',
                            right: 0,
                            marginTop: theme.spacing.xs,
                            backgroundColor: theme.colors.surface,
                            border: `1px solid ${theme.colors.border.default}`,
                            borderRadius: theme.borderRadius.md,
                            boxShadow: theme.shadows.md,
                            width: 'min(360px, calc(100vw - 32px))',
                            maxHeight: 'min(420px, 70vh)',
                            zIndex: 1000,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                borderBottom: `1px solid ${theme.colors.border.default}`,
                            }}
                        >
                            <span style={{
                                fontSize: theme.typography.fontSize.sm,
                                fontWeight: theme.typography.fontWeight.semibold,
                                color: theme.colors.text.primary,
                            }}>
                                Notifications
                            </span>
                            <button
                                type="button"
                                onClick={() => setShowPanel(false)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: theme.spacing.xs,
                                    color: theme.colors.text.secondary,
                                    display: 'flex',
                                    alignItems: 'center',
                                    borderRadius: theme.borderRadius.sm,
                                    lineHeight: 1,
                                }}
                                onMouseEnter={e => { e.currentTarget.style.color = theme.colors.text.primary; }}
                                onMouseLeave={e => { e.currentTarget.style.color = theme.colors.text.secondary; }}
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div
                            style={{
                                overflowY: 'auto',
                                flex: 1,
                                minHeight: 0,
                            }}
                        >
                            {loading ? (
                                <div
                                    style={{
                                        padding: theme.spacing.lg,
                                        fontSize: theme.typography.fontSize.sm,
                                        color: theme.colors.text.secondary,
                                    }}
                                >
                                    Loading…
                                </div>
                            ) : items.length === 0 ? (
                                <div
                                    style={{
                                        padding: theme.spacing.lg,
                                        fontSize: theme.typography.fontSize.sm,
                                        color: theme.colors.text.secondary,
                                    }}
                                >
                                    No unread notifications
                                </div>
                            ) : (
                                items.map(n => {
                                    const busy = togglingIds.has(n.notificationId);
                                    return (
                                        <div
                                            key={n.notificationId}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => handleRowClick(n)}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    handleRowClick(n);
                                                }
                                            }}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: theme.spacing.sm,
                                                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                                borderBottom: `1px solid ${theme.colors.border.default}`,
                                                cursor: 'pointer',
                                                backgroundColor: theme.colors.surface,
                                                transition: `background-color ${theme.transitions.fast}`,
                                            }}
                                            onMouseEnter={e => {
                                                e.currentTarget.style.backgroundColor =
                                                    theme.colors.surfaceSecondary;
                                            }}
                                            onMouseLeave={e => {
                                                e.currentTarget.style.backgroundColor = theme.colors.surface;
                                            }}
                                        >
                                            <CustomTooltip text="Mark as read">
                                            <button
                                                type="button"
                                                disabled={busy}
                                                onClick={e => handleMarkRead(n, e)}
                                                style={{
                                                    flexShrink: 0,
                                                    marginTop: 2,
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: busy ? 'wait' : 'pointer',
                                                    padding: theme.spacing.xs,
                                                    borderRadius: theme.borderRadius.sm,
                                                    opacity: busy ? 0.5 : 1,
                                                }}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faEnvelope}
                                                    style={{
                                                        fontSize: theme.typography.fontSize.md,
                                                        color: theme.colors.primary,
                                                    }}
                                                />
                                            </button>
                                            </CustomTooltip>
                                            <div style={{ minWidth: 0, flex: 1 }}>
                                                <div
                                                    style={{
                                                        fontSize: theme.typography.fontSize.sm,
                                                        fontWeight: theme.typography.fontWeight.semibold,
                                                        color: theme.colors.text.primary,
                                                        marginBottom: theme.spacing.xs,
                                                    }}
                                                >
                                                    {truncate(n.title, 80)}
                                                </div>
                                                <div
                                                    style={{
                                                        fontSize: theme.typography.fontSize.xs,
                                                        color: theme.colors.text.secondary,
                                                        lineHeight: 1.35,
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {n.messageFormat === 'html'
                                                        ? truncate(htmlToPlainText(n.message), 240)
                                                        : renderTextWithLinks(truncate(n.message, 240), navigateTo, theme)}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                gap: theme.spacing.sm,
                                padding: theme.spacing.md,
                                borderTop: `1px solid ${theme.colors.border.default}`,
                                justifyContent: 'flex-end',
                            }}
                        >
                            <button
                                type="button"
                                disabled={(items.length === 0 && unreadNotificationCount === 0) || loading || markingAllRead}
                                onClick={() => void handleMarkAllRead()}
                                style={{
                                    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                    backgroundColor: theme.colors.primary,
                                    color: theme.colors.text.inverse || '#fff',
                                    border: 'none',
                                    borderRadius: theme.borderRadius.sm,
                                    fontSize: theme.typography.fontSize.sm,
                                    fontWeight: theme.typography.fontWeight.medium,
                                    cursor: (items.length === 0 && unreadNotificationCount === 0) || loading || markingAllRead ? 'not-allowed' : 'pointer',
                                    opacity: (items.length === 0 && unreadNotificationCount === 0) || loading || markingAllRead ? 0.6 : 1,
                                }}
                            >
                                Mark All as Read
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowPanel(false);
                                    navigateTo('/notifications');
                                }}
                                style={{
                                    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                    backgroundColor: theme.colors.primary,
                                    color: theme.colors.text.inverse || '#fff',
                                    border: 'none',
                                    borderRadius: theme.borderRadius.sm,
                                    fontSize: theme.typography.fontSize.sm,
                                    fontWeight: theme.typography.fontWeight.medium,
                                    cursor: 'pointer',
                                }}
                            >
                                View All
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <NotificationModal
                isOpen={selectedNotification !== null}
                onClose={handleCloseNotificationModal}
                title={selectedNotification?.title ?? ''}
                message={selectedNotification?.message ?? ''}
                messageFormat={selectedNotification?.messageFormat}
                notificationId={selectedNotification?.notificationId}
                linkUrl={selectedNotification?.linkUrl}
            />

            <SaveConfirmationModal
                isVisible={showSaveModal}
                onSave={handleSave}
                onDiscard={handleDiscard}
                onCancel={handleCancel}
                isSaving={isSavingBeforeNav}
            />
        </>
    );
};
